import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import StatusBadge from "@/components/StatusBadge";
import ProgressTracker from "@/components/ProgressTracker";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Sparkles, Clock, Trash2, Paperclip, X, Wand2, Loader2, Tag, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NOTE_CHIPS_AR = [
  "أضف فصلاً عن التطبيقات العملية",
  "ركّز على المراجع العربية",
  "أضف خاتمة موجزة",
  "اجعل الأسلوب أكاديمياً رسمياً",
  "اجعل لغة البحث أبسط",
  "بدون معادلات رياضية",
];
const NOTE_CHIPS_EN = [
  "Add a chapter on practical applications",
  "Emphasize peer-reviewed sources",
  "Add a concise conclusion",
  "Use a formal academic tone",
  "Use simpler language",
  "No mathematical equations",
];

const MAX_ATTACH_BYTES = 5 * 1024 * 1024;
const ATTACH_EXTS = [".pdf", ".docx", ".txt"];

// Research methodology code → readable label.
const METHODOLOGY_LABELS = {
  ar: {
    auto: "عام / تلقائي",
    systematic_review: "مراجعة منهجية",
    quantitative: "دراسة كمية",
    qualitative: "دراسة نوعية",
    other: "أخرى",
  },
  en: {
    auto: "General / Auto",
    systematic_review: "Systematic Review",
    quantitative: "Quantitative Study",
    qualitative: "Qualitative Study",
    other: "Other",
  },
};
const methodologyLabel = (m, custom, lang) => {
  if (m === "other" && custom) return custom;
  return (METHODOLOGY_LABELS[lang] || METHODOLOGY_LABELS.ar)[m] || m;
};

// Critical appraisal tool code → short display label (for summary/archive).
const APPRAISAL_LABELS = {
  casp: "CASP",
  cochrane_rob2: "Cochrane RoB 2",
  robins_i: "ROBINS-I",
  jbi: "JBI",
  nos: "NOS",
  amstar2: "AMSTAR 2",
  axis: "AXIS",
  other: { ar: "أخرى", en: "Other" },
};
const appraisalLabel = (tool, lang) => {
  const v = APPRAISAL_LABELS[tool];
  if (!v) return tool;
  return typeof v === "string" ? v : v[lang] || v.ar;
};

// --- Inference code → readable label maps -----------------------------------
// Backend returns short technical codes like "computer_science" / "ar"; we
// translate them on the client side so the user sees natural Arabic
// (or English when they toggle EN). Unknown codes fall back to the raw code.
const FIELD_LABELS = {
  ar: {
    computer_science: "علوم الحاسب",
    information_technology: "تقنية المعلومات",
    artificial_intelligence: "الذكاء الاصطناعي",
    data_science: "علم البيانات",
    cybersecurity: "الأمن السيبراني",
    telecommunications: "الاتصالات",
    engineering: "الهندسة",
    business: "إدارة الأعمال",
    economics: "الاقتصاد",
    marketing: "التسويق",
    finance: "المالية",
    education: "التربية والتعليم",
    medicine: "الطب",
    nursing: "التمريض",
    psychology: "علم النفس",
    sociology: "علم الاجتماع",
    law: "القانون",
    sharia: "الشريعة",
    history: "التاريخ",
    geography: "الجغرافيا",
    literature: "الأدب",
    linguistics: "اللغويات",
    media: "الإعلام",
    environment: "البيئة",
    sports: "الرياضة",
    general: "عام",
  },
  en: {
    computer_science: "Computer Science", information_technology: "IT",
    artificial_intelligence: "Artificial Intelligence", data_science: "Data Science",
    cybersecurity: "Cybersecurity", telecommunications: "Telecommunications",
    engineering: "Engineering", business: "Business", economics: "Economics",
    marketing: "Marketing", finance: "Finance", education: "Education",
    medicine: "Medicine", nursing: "Nursing", psychology: "Psychology",
    sociology: "Sociology", law: "Law", sharia: "Sharia", history: "History",
    geography: "Geography", literature: "Literature", linguistics: "Linguistics",
    media: "Media", environment: "Environment", sports: "Sports", general: "General",
  },
};
const LANG_LABELS = {
  ar: { ar: "العربية", en: "الإنجليزية" },
  en: { ar: "Arabic", en: "English" },
};
const labelField = (code, uiLang) =>
  (FIELD_LABELS[uiLang] || FIELD_LABELS.ar)[code] || code || "—";
const labelLanguage = (code, uiLang) =>
  (LANG_LABELS[uiLang] || LANG_LABELS.ar)[code] || code || "—";

const NewOrderTab = () => {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("ar");
  const [methodology, setMethodology] = useState("auto");
  const [customMethodology, setCustomMethodology] = useState("");
  const [criticalAppraisal, setCriticalAppraisal] = useState("");
  // Admin-configurable page limits. We load the live config once on mount;
  // default to 10–20 (matches backend default) so the slider is never empty.
  const [pricingCfg, setPricingCfg] = useState({ pages_min: 10, pages_max: 20, usd_per_page: 1 });
  const [pages, setPages] = useState(10);
  const [notes, setNotes] = useState("");
  const [inferred, setInferred] = useState(null);
  const [titleSuggestionDismissed, setTitleSuggestionDismissed] = useState(false);
  const [quote, setQuote] = useState(null);
  const [acceptNoRefund, setAcceptNoRefund] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  // File attachment state
  const [attachment, setAttachment] = useState(null); // { id, filename, size }
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = React.useRef(null);

  const [inferring, setInferring] = useState(false);

  const chips = lang === "ar" ? NOTE_CHIPS_AR : NOTE_CHIPS_EN;

  const onInfer = async () => {
    setErr("");
    if (title.trim().length < 3 || inferring) return;
    setInferring(true);
    try {
      const { data } = await api.post("/orders/inference", { title });
      setInferred(data);
      setLanguage(data.inferred_language);
      setTitleSuggestionDismissed(false); // re-show suggestion for each new analysis
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setInferring(false);
    }
  };

  const acceptTitleSuggestion = () => {
    if (inferred?.suggested_title) {
      setTitle(inferred.suggested_title);
      setTitleSuggestionDismissed(true);
      toast.success(
        lang === "ar" ? "تم اعتماد العنوان المقترح" : "Suggested title applied"
      );
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!f) return;
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ATTACH_EXTS.includes(ext)) {
      toast.error(t("order_attach_bad_type"));
      return;
    }
    if (f.size > MAX_ATTACH_BYTES) {
      toast.error(t("order_attach_too_big"));
      return;
    }
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/orders/attachment/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment({
        id: data.attachment_id,
        filename: data.filename,
        size: data.size_bytes,
        chars: data.extracted_chars,
      });
      toast.success(t("order_attach_ready"));
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = () => setAttachment(null);

  const fetchQuote = async () => {
    try {
      const { data } = await api.post("/orders/quote", {
        title: title || "placeholder",
        language,
        page_count: pages,
      });
      setQuote(data);
    } catch (e) {
      // Quote refresh is non-critical — slider keeps the last good price.
      // Surface the error for debugging without blocking the UX.
      console.error("[quote] refresh failed:", e);
    }
  };

  useEffect(() => {
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  // Load admin-configurable page limits + per-page price ONCE so the slider
  // snaps to the admin's range and the price preview reflects the live
  // platform setting. If the admin changes the floor (e.g. 10 → 12) the
  // current `pages` is clamped into the new window.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/orders/pricing-config")
      .then(({ data }) => {
        if (cancelled) return;
        setPricingCfg(data);
        setPages((p) => Math.min(data.pages_max, Math.max(data.pages_min, p)));
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const toggleChip = (chip) => {
    setNotes((n) => {
      if (n.includes(chip)) return n.replace(chip, "").trim();
      return (n ? n + "\n" : "") + chip;
    });
  };

  const COUPON_MSG = {
    coupon_invalid: { ar: "كود الخصم غير صحيح أو غير مُفعّل.", en: "Invalid or inactive coupon." },
    coupon_expired: { ar: "انتهت صلاحية كود الخصم.", en: "This coupon has expired." },
    coupon_exhausted: { ar: "تم استنفاد عدد مرات استخدام هذا الكود.", en: "Coupon usage limit reached." },
    coupon_used_by_user: { ar: "لقد استخدمت هذا الكود من قبل.", en: "You already used this coupon." },
  };

  // Re-validating a coupon depends on page count, so a page change clears it.
  useEffect(() => { setCouponPreview(null); }, [pages]);

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/orders/preview-coupon", { code, page_count: pages });
      setCouponPreview(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const m = COUPON_MSG[detail];
      setCouponError(m ? m[lang === "ar" ? "ar" : "en"] : formatApiError(e));
      setCouponPreview(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponPreview(null);
    setCouponCode("");
    setCouponError("");
  };

  const onSubmit = async () => {
    setErr("");
    if (!acceptNoRefund) {
      setErr(t("confirm_terms_no_refund"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        title,
        language,
        page_count: pages,
        additional_notes: notes || null,
        methodology,
        custom_methodology: methodology === "other" ? (customMethodology.trim() || null) : null,
        critical_appraisal_tool: methodology === "systematic_review" ? (criticalAppraisal || null) : null,
        inferred_field: inferred?.inferred_field,
        inferred_language: inferred?.inferred_language,
        attachment_id: attachment?.id || null,
      });
      const orderId = data.order.id;
      if (user?.role === "admin") {
        // Admin free-testing path: skip payment entirely.
        await api.post(`/orders/${orderId}/mock-pay`);
        setCreated({ ...data.order, status: "pending", is_test: true });
        toast.success(
          lang === "ar"
            ? "تم وضع البحث في الطابور — بدون دفع (وضع الأدمن التجريبي)"
            : "Queued as admin free-test (no payment)"
        );
      } else {
        // Apply a validated coupon to the freshly-created order so the Moyasar
        // amount is already discounted server-side before the pay page loads.
        let isFree = false;
        if (couponPreview && couponCode.trim()) {
          try {
            const { data: applied } = await api.post(`/orders/${orderId}/apply-coupon`, { code: couponCode.trim() });
            isFree = applied?.free === true || Number(applied?.effective_sar) === 0;
          } catch (_) {
            toast.warning(
              lang === "ar"
                ? "تعذّر تطبيق الكوبون تلقائياً — يمكنك إعادة إدخاله في صفحة الدفع."
                : "Couldn't auto-apply the coupon — you can re-enter it on the payment page."
            );
          }
        }
        if (isFree) {
          // Free coupon → skip payment entirely; enqueue via free checkout.
          await api.post(`/orders/${orderId}/checkout-free`);
          setCreated({ ...data.order, status: "pending" });
          toast.success(
            lang === "ar"
              ? "تم تفعيل الكوبون المجاني ووضع بحثك في الطابور — بدون دفع 🎉"
              : "Free coupon applied — your paper is queued (no payment) 🎉"
          );
        } else {
          // Real user: redirect to Moyasar payment page.
          toast.info(
            lang === "ar" ? "نوجّهك إلى صفحة الدفع…" : "Redirecting to payment…"
          );
          navigate(`/pay/${orderId}`);
        }
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Card className="p-8 border border-slate-200 shadow-none" data-testid="order-created-card">
        <div className="flex items-start gap-3 mb-4">
          <FileText className="w-6 h-6 text-slate-900" />
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">{created.title}</h3>
            <p className="text-xs text-slate-500 mt-1">
              {created.id} · <StatusBadge status={created.status} />
            </p>
          </div>
        </div>
        <Alert className="bg-amber-50 border-amber-200">
          <AlertDescription className="text-amber-900 text-sm">
            {lang === "ar"
              ? "تم استلام طلبك بنجاح ووضعه في طابور التوليد. ستجده في طلباتي بحالة \"قيد المعالجة\"، وعند الاكتمال يمكنك المعاينة والتنزيل من هناك."
              : "Order accepted and queued for generation. Track it in Archive: status will move to 'Processing' then 'Ready'."}
          </AlertDescription>
        </Alert>
        <Button
          onClick={() => setCreated(null)}
          className="mt-6 bg-slate-900 hover:bg-slate-800"
          data-testid="order-create-another-btn"
        >
          {lang === "ar" ? "طلب آخر" : "New order"}
        </Button>
      </Card>
    );
  }

  return (
    <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">
      <Card className="lg:col-span-2 p-5 sm:p-8 border border-slate-200 shadow-none">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-3">
          {t("tab_new_order")}
        </h2>

        <Alert className="mb-6 bg-emerald-50 border-emerald-200" data-testid="expectation-box">
          <AlertDescription className="text-emerald-900 text-sm leading-relaxed text-start">
            <b>{lang === "ar" ? "ما ستحصل عليه: " : "What you'll get: "}</b>
            {lang === "ar"
              ? "مسوّدةٌ أكاديميةٌ احترافيةٌ متكاملة، تحتاج لمراجعتك وتدقيق مراجعها قبل التسليم النهائي."
              : "A complete professional academic draft — you should still review the content and verify references before final submission."}
          </AlertDescription>
        </Alert>

        {err && (
          <Alert variant="destructive" className="mb-4" data-testid="order-error-alert">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          <div className="space-y-2">
            {/* Beginner-friendly 3-step helper (mobile-first) */}
            <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 text-[13px] leading-relaxed text-indigo-900 text-start" data-testid="title-helper-steps">
              <p className="font-bold mb-1">{lang === "ar" ? "كيف تبدأ؟" : "How to start?"}</p>
              {lang === "ar" ? (
                <ol className="ps-5 list-decimal space-y-0.5">
                  <li>اكتب عنوان بحثك (ولو بصياغة بسيطة).</li>
                  <li>اضغط (تأكيد العنوان ورؤية المقترحات).</li>
                  <li>اعتمد العنوان المقترح أو احتفظ بعنوانك، ثم تابِع.</li>
                </ol>
              ) : (
                <ol className="ps-5 list-decimal space-y-0.5">
                  <li>Write your research title (even a rough draft is OK).</li>
                  <li>Click "Confirm title & see suggestions".</li>
                  <li>Accept the suggestion or keep yours, then continue.</li>
                </ol>
              )}
            </div>

            <Label htmlFor="order-title" className="text-start block">{t("order_title_label")}</Label>
            {/* Mobile-first: full-width input, button STACKED below.
                On ≥sm screens: side-by-side. iOS auto-zoom avoided with 16px font. */}
            <Input
              id="order-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={onInfer}
              data-testid="order-title-input"
              className="w-full text-base h-12"
              style={{ fontSize: "16px" }}
              placeholder={lang === "ar" ? "اكتب عنوان بحثك هنا..." : "Write your research title here..."}
            />
            <Button
              type="button"
              onClick={onInfer}
              disabled={inferring || title.trim().length < 3}
              data-testid="order-infer-btn"
              className="gap-1.5 w-full sm:w-auto min-h-[44px] bg-slate-900 hover:bg-slate-800 text-white"
            >
              {inferring ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {lang === "ar" ? "جارٍ التحليل…" : "Analyzing…"}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {lang === "ar" ? "تأكيد العنوان ورؤية المقترحات" : "Confirm title & see suggestions"}
                </>
              )}
            </Button>
            {inferring && (
              <div
                className="flex items-center gap-2 mt-2 px-3 py-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 text-sm"
                data-testid="order-infer-loading"
                aria-live="polite"
              >
                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                <span>
                  {lang === "ar"
                    ? "جارٍ تحليل العنوان… قد يستغرق ذلك بضع ثوانٍ"
                    : "Analyzing title… this can take a few seconds"}
                </span>
              </div>
            )}
            {inferred && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="order-inference-result">
                <span className="text-xs px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md">
                  {t("inferred_language")}: <b>{labelLanguage(inferred.inferred_language, lang)}</b>
                </span>
                <span className="text-xs px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md">
                  {t("inferred_field")}: <b>{labelField(inferred.inferred_field, lang)}</b>
                </span>
              </div>
            )}

            {inferred?.suggested_title && !titleSuggestionDismissed && inferred.suggested_title !== title && (
              <div
                className="mt-3 p-4 rounded-lg border border-indigo-200 bg-indigo-50/70"
                data-testid="title-suggestion-card"
              >
                <div className="flex items-start gap-3">
                  <Wand2 className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-indigo-800 mb-1 text-start">
                      {t("title_suggestion_label")}
                    </p>
                    <p
                      className="text-sm text-slate-900 font-medium leading-relaxed text-start"
                      data-testid="title-suggestion-text"
                    >
                      {inferred.suggested_title}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        type="button"
                        size="sm"
                        onClick={acceptTitleSuggestion}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="title-suggestion-accept-btn"
                      >
                        {t("title_suggestion_accept")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setTitleSuggestionDismissed(true)}
                        data-testid="title-suggestion-keep-btn"
                      >
                        {t("title_suggestion_keep")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-start block">{t("order_language_label")}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="order-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ar">العربية</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-start block">
                {t("order_page_count_label")}: <b data-testid="order-pages-value">{pages}</b>
                <span className="text-xs text-slate-400 ms-2">
                  ({pricingCfg.pages_min}–{pricingCfg.pages_max})
                </span>
              </Label>
              <Slider
                min={pricingCfg.pages_min}
                max={pricingCfg.pages_max}
                step={1}
                value={[pages]}
                onValueChange={(v) => setPages(v[0])}
                data-testid="order-pages-slider"
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="order-methodology-section">
            <Label className="text-start block">{t("order_methodology_label")}</Label>
            <Select
              value={methodology}
              onValueChange={(v) => {
                setMethodology(v);
                if (v !== "other") setCustomMethodology("");
                if (v !== "systematic_review") setCriticalAppraisal("");
              }}
            >
              <SelectTrigger data-testid="order-methodology-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{lang === "ar" ? "عام / تلقائي" : "General / Auto"}</SelectItem>
                <SelectItem value="systematic_review">{lang === "ar" ? "مراجعة منهجية" : "Systematic Review"}</SelectItem>
                <SelectItem value="quantitative">{lang === "ar" ? "دراسة كمية" : "Quantitative Study"}</SelectItem>
                <SelectItem value="qualitative">{lang === "ar" ? "دراسة نوعية" : "Qualitative Study"}</SelectItem>
                <SelectItem value="other">{lang === "ar" ? "أخرى - يرجى التحديد" : "Other - Please specify"}</SelectItem>
              </SelectContent>
            </Select>
            {methodology === "other" && (
              <Input
                value={customMethodology}
                onChange={(e) => setCustomMethodology(e.target.value)}
                placeholder={lang === "ar" ? "اكتب المنهجية المخصصة هنا..." : "Enter custom methodology here..."}
                maxLength={300}
                data-testid="order-custom-methodology-input"
                className="mt-2"
              />
            )}
          </div>

          {methodology === "systematic_review" && (
            <div className="space-y-2" data-testid="order-appraisal-section">
              <Label className="text-start block">{t("order_appraisal_tool_label")}</Label>
              <Select value={criticalAppraisal || undefined} onValueChange={setCriticalAppraisal}>
                <SelectTrigger data-testid="order-appraisal-select">
                  <SelectValue placeholder={lang === "ar" ? "اختر الأداة المناسبة لتقييم الجودة..." : "Select an appraisal tool..."} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casp">{lang === "ar" ? "أداة CASP (برنامج مهارات التقييم النقدي)" : "CASP (Critical Appraisal Skills Programme)"}</SelectItem>
                  <SelectItem value="cochrane_rob2">{lang === "ar" ? "أداة Cochrane RoB 2 (للتجارب العشوائية RCTs)" : "Cochrane RoB 2 (RCTs)"}</SelectItem>
                  <SelectItem value="robins_i">{lang === "ar" ? "أداة ROBINS-I (للأبحاث غير العشوائية)" : "ROBINS-I (non-randomized)"}</SelectItem>
                  <SelectItem value="jbi">{lang === "ar" ? "أدوات معهد جوانا بريجز (JBI Tools)" : "JBI Tools"}</SelectItem>
                  <SelectItem value="nos">{lang === "ar" ? "مقياس نيوكاسل-أوتاوا (NOS Scale)" : "Newcastle-Ottawa Scale (NOS)"}</SelectItem>
                  <SelectItem value="amstar2">{lang === "ar" ? "أداة AMSTAR 2 (لتقييم المراجعات المنهجية)" : "AMSTAR 2 (systematic reviews)"}</SelectItem>
                  <SelectItem value="axis">{lang === "ar" ? "أداة AXIS (للدراسات المقطعية Cross-Sectional)" : "AXIS (cross-sectional)"}</SelectItem>
                  <SelectItem value="other">{lang === "ar" ? "أخرى (يرجى التحديد في الملاحظات)" : "Other (specify in notes)"}</SelectItem>
                </SelectContent>
              </Select>
              {criticalAppraisal === "other" && (
                <p className="text-xs text-amber-600 pt-1" data-testid="order-appraisal-other-hint">
                  {lang === "ar"
                    ? "يرجى تحديد أداة التقييم في خانة «ملاحظات إضافية» أدناه."
                    : "Please specify the appraisal tool in the Additional Notes field below."}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-start block">{t("order_notes_label")}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="order-notes-input"
            />
            <div className="flex flex-wrap gap-2 pt-1">
              {chips.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => toggleChip(c)}
                  className={`text-xs px-3 py-1 border rounded-full transition-all ${notes.includes(c) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`order-note-chip-${c.slice(0, 12)}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed pt-2">{t("order_notes_hint")}</p>
          </div>

          <div className="space-y-2" data-testid="order-attachment-section">
            <Label className="text-start block">{t("order_attach_label")}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={onFileChosen}
              className="hidden"
              data-testid="order-attachment-file-input"
            />
            {!attachment ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPickFile}
                  disabled={uploadingAttachment}
                  className="gap-1.5"
                  data-testid="order-attachment-pick-btn"
                >
                  <Paperclip className="w-4 h-4" />
                  {uploadingAttachment ? t("order_attach_uploading") : t("order_attach_btn")}
                </Button>
                <span className="text-xs text-slate-500">{t("order_attach_hint")}</span>
              </div>
            ) : (
              <div
                className="flex items-center justify-between gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50"
                data-testid="order-attachment-ready"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span
                    className="text-sm text-emerald-900 font-medium truncate"
                    data-testid="order-attachment-filename"
                  >
                    {attachment.filename}
                  </span>
                  <span className="text-[11px] text-emerald-700 whitespace-nowrap">
                    {(attachment.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={removeAttachment}
                  className="text-emerald-800 hover:text-emerald-900 hover:bg-emerald-100"
                  data-testid="order-attachment-remove-btn"
                >
                  <X className="w-4 h-4" />
                  {t("order_attach_remove")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* SUMMARY */}
      <Card className="p-5 sm:p-8 border border-slate-200 shadow-none h-fit lg:sticky lg:top-20">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
          {t("confirm_summary")}
        </h3>
        <dl className="space-y-3 text-sm text-slate-700">
          <div className="flex justify-between">
            <dt className="text-slate-500">{t("order_language_label")}</dt>
            <dd className="font-medium">{language === "ar" ? "العربية" : "English"}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">{t("order_page_count_label")}</dt>
            <dd className="font-medium">{pages}</dd>
          </div>
          {methodology !== "auto" && (
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("order_methodology_label")}</dt>
              <dd className="font-medium text-end" data-testid="order-summary-methodology">
                {methodologyLabel(methodology, customMethodology, lang)}
              </dd>
            </div>
          )}
          {methodology === "systematic_review" && criticalAppraisal && (
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("order_appraisal_tool_label")}</dt>
              <dd className="font-medium text-end" data-testid="order-summary-appraisal">
                {appraisalLabel(criticalAppraisal, lang)}
              </dd>
            </div>
          )}
          {quote && (
            <div className="flex justify-between border-t border-slate-200 pt-3">
              <dt className="text-slate-500">{t("cost_label")}</dt>
              <dd className="font-bold text-slate-900" data-testid="order-quote-cost">
                ${quote.price_usd} <span className="text-xs text-slate-500">({quote.price_sar} SAR)</span>
              </dd>
            </div>
          )}
        </dl>

        {quote && (
          <div className="mt-5 border-t border-slate-200 pt-4" data-testid="order-coupon-section">
            {!couponPreview ? (
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2">
                  <Tag className="w-4 h-4 text-slate-500" />
                  {lang === "ar" ? "هل لديك كود خصم؟" : "Have a coupon?"}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                    placeholder={lang === "ar" ? "أدخل الكود" : "Enter code"}
                    data-testid="order-coupon-input"
                    className="h-10 uppercase"
                  />
                  <Button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon || !couponCode.trim()}
                    data-testid="order-coupon-apply-btn"
                    variant="outline"
                    className="h-10 shrink-0 px-4 border-slate-300"
                  >
                    {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "ar" ? "تطبيق" : "Apply")}
                  </Button>
                </div>
                {couponError && <p className="text-xs text-red-600 mt-2" data-testid="order-coupon-error">{couponError}</p>}
              </div>
            ) : (
              <div data-testid="order-coupon-applied" className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
                    <Check className="w-4 h-4" /> {couponPreview.code}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeCoupon}
                    data-testid="order-coupon-remove-btn"
                    className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                  >
                    {lang === "ar" ? "إزالة" : "Remove"}
                  </Button>
                </div>
                <div className="flex justify-between text-sm text-emerald-700">
                  <span>{lang === "ar" ? "الخصم" : "Discount"}</span>
                  <span>−{couponPreview.discount_sar.toFixed(2)} SAR</span>
                </div>
                <div className="flex justify-between text-base font-bold text-slate-900">
                  <span>{lang === "ar" ? "الإجمالي بعد الخصم" : "Total after discount"}</span>
                  <span data-testid="order-coupon-total">
                    {couponPreview.free
                      ? (lang === "ar" ? "مجاني بالكامل 🎉" : "Completely free 🎉")
                      : `${couponPreview.effective_sar.toFixed(2)} SAR`}
                  </span>
                </div>
                {couponPreview.min_floor_applied && (
                  <p className="text-[11px] text-amber-700">
                    {lang === "ar"
                      ? `الحد الأدنى للدفع ${couponPreview.min_payable_sar} ريال.`
                      : `Minimum payable is ${couponPreview.min_payable_sar} SAR.`}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-3 mt-6">
          <Checkbox
            id="order-norefund"
            checked={acceptNoRefund}
            onCheckedChange={(v) => setAcceptNoRefund(!!v)}
            data-testid="order-norefund-checkbox"
            className="mt-0.5"
          />
          <Label
            htmlFor="order-norefund"
            className="text-xs text-slate-700 leading-relaxed font-normal text-start cursor-pointer"
          >
            {t("confirm_terms_no_refund")}
          </Label>
        </div>

        <Button
          onClick={onSubmit}
          disabled={busy || !title || !acceptNoRefund}
          className="w-full mt-5 bg-slate-900 hover:bg-slate-800 h-12 text-base"
          data-testid="order-submit-btn"
        >
          {busy ? <Clock className="w-4 h-4 animate-spin" /> : (
            couponPreview?.free
              ? (lang === "ar" ? "احصل على بحثك مجاناً 🎉" : "Get your paper for free 🎉")
              : t("confirm_pay_btn")
          )}
        </Button>
        <p className="text-[10px] text-slate-400 mt-2 text-center" data-testid="batch1-note">
          {lang === "ar" ? "الدفع آمن ومشفّر عبر بوابة الدفع المعتمدة" : "Secure encrypted payment via our certified gateway"}
        </p>
      </Card>
    </div>
  );
};

const ArchiveTab = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data.orders);
    } catch (e) {
      setOrders([]);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh while any order is in flight, so users see status changes.
    const id = setInterval(() => {
      setOrders((prev) => {
        if (!prev) return prev;
        const inflight = prev.some((o) =>
          ["pending", "processing", "awaiting_payment"].includes(o.status)
        );
        if (inflight) load();
        return prev;
      });
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const openPreview = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const retry = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/retry`);
      toast.success(
        (lang === "ar" ? "تمت إعادة الجدولة" : "Retry queued") +
          (data.from_saved_text
            ? lang === "ar"
              ? " (من النص المحفوظ — بدون تكلفة LLM)"
              : " (from saved text — no LLM cost)"
            : "")
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareWhatsApp = async (o) => {
    // STRATEGY: attach the .docx file itself via Web Share API. On iOS/Android
    // with WhatsApp installed, the user picks WhatsApp from the share sheet
    // and the Word document is sent as an attachment — exactly what they
    // want. We fall back to a link-only share (and ultimately wa.me) when
    // the OS doesn't support file sharing.

    const messageText = t("share_whatsapp_message").replace(
      "{title}", o.title || "بحثي"
    );

    // 1) Try sharing the actual file
    try {
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/orders/${o.id}/file`;
      const resp = await fetch(fileUrl, { credentials: "include" });
      if (resp.ok) {
        const blob = await resp.blob();
        const file = new File(
          [blob],
          `${(o.title || "paper").slice(0, 60)}.docx`,
          {
            type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          }
        );
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: o.title || "زبطنا",
              text: messageText,
              files: [file],
            });
            return;
          } catch (err) {
            if (err && err.name === "AbortError") return;
            // fall through to link-share
          }
        }
      }
    } catch (err) {
      // Web Share API not available or blocked — fall through to link share.
      console.warn("[share] file-share failed, falling back to link:", err);
    }

    // 2) Fallback: share the public download LINK (no file). Useful when
    // file sharing is unsupported (e.g. WhatsApp Web on desktop).
    let shareUrl;
    try {
      const { data } = await api.post(`/orders/${o.id}/share-link`);
      shareUrl = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      return;
    }
    const fullText = `${messageText} ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: o.title || "زبطنا",
          text: fullText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }

    const isMobile = /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent);
    const url = isMobile
      ? `whatsapp://send?text=${encodeURIComponent(fullText)}`
      : `https://web.whatsapp.com/send?text=${encodeURIComponent(fullText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/orders/${pendingDelete.id}`);
      toast.success(t("delete_success"));
      setOrders((prev) => (prev ? prev.filter((x) => x.id !== pendingDelete.id) : prev));
      setPendingDelete(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (orders === null) return <p className="text-sm text-slate-500">{t("loading")}</p>;
  if (orders.length === 0) {
    return (
      <Card className="p-12 border border-slate-200 shadow-none text-center" data-testid="archive-empty-card">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">{t("archive_empty")}</p>
      </Card>
    );
  }
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="archive-grid">
        {orders.map((o) => (
          <Card key={o.id} className="p-5 sm:p-6 border border-slate-200 shadow-none" data-testid={`archive-card-${o.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-heading font-semibold text-slate-900 line-clamp-2 text-start">
                {o.title}
              </h3>
              <StatusBadge status={o.status} />
            </div>
            <p className="text-xs text-slate-500 text-start mb-3">
              {o.language === "ar" ? "العربية" : "English"} · {o.page_count} {lang === "ar" ? "صفحة" : "pages"} · ${o.price_usd}
            </p>
            {o.methodology && o.methodology !== "auto" && (
              <p className="text-[11px] text-start mb-3 flex flex-wrap gap-1.5" data-testid={`archive-methodology-${o.id}`}>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  {methodologyLabel(o.methodology, o.custom_methodology, lang)}
                </span>
                {o.critical_appraisal_tool && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium" data-testid={`archive-appraisal-${o.id}`}>
                    {appraisalLabel(o.critical_appraisal_tool, lang)}
                  </span>
                )}
              </p>
            )}
            {o.references_verified_count != null && (
              <p className="text-[11px] text-slate-500 text-start mb-3" data-testid={`archive-refs-${o.id}`}>
                {lang === "ar" ? "مراجع متحقَّق منها:" : "Verified refs:"}{" "}
                <b>{o.references_verified_count}/{o.references_candidates_count ?? 0}</b>
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              {o.status === "awaiting_payment" && (
                <Button
                  size="sm"
                  onClick={() => navigate(`/pay/${o.id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`archive-resume-pay-${o.id}`}
                >
                  {t("resume_payment_btn")}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={!o.raw_text && o.status !== "completed"}
                onClick={() => openPreview(o)}
                data-testid={`archive-preview-${o.id}`}
              >
                {t("preview_btn")}
              </Button>
              {o.status === "completed" ? (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/orders/${o.id}/file`}
                  rel="noopener"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                  data-testid={`archive-download-${o.id}`}
                >
                  {t("download_btn")}
                </a>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled
                  data-testid={`archive-download-${o.id}`}
                >
                  {t("download_btn")}
                </Button>
              )}
              {o.status === "completed" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => shareWhatsApp(o)}
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-400"
                  data-testid={`archive-share-whatsapp-${o.id}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-3.5 h-3.5 me-1"
                    aria-hidden="true"
                  >
                    <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.7-.9-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.6 0 1.5 1.1 3 1.3 3.2.2.3 2.2 3.4 5.3 4.7.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.6 1.4 5.1L2 22l5-1.4c1.4.8 3.1 1.2 4.8 1.2h.2c5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.3c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.1.8.8-3-.2-.3c-.9-1.4-1.3-3-1.3-4.6 0-4.6 3.8-8.4 8.4-8.4 2.3 0 4.4.9 5.9 2.5 1.6 1.6 2.5 3.7 2.5 5.9.1 4.7-3.6 8.5-8.3 8.5z" />
                  </svg>
                  {t("share_whatsapp_btn")}
                </Button>
              )}
              {o.status === "failed" && (
                <Button
                  size="sm"
                  onClick={() => retry(o)}
                  data-testid={`archive-retry-${o.id}`}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {t("retry_btn")}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={["pending", "processing"].includes(o.status)}
                onClick={() => setPendingDelete(o)}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid={`archive-delete-${o.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 me-1" />
                {t("delete_btn")}
              </Button>
              {o.refund_required && (
                <span
                  className="text-[11px] px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md"
                  data-testid={`archive-refund-${o.id}`}
                >
                  {lang === "ar" ? "مستحق استرداد" : "Refund pending"}
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-start">{preview?.title}</DialogTitle>
          </DialogHeader>
          <pre
            className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-sans text-start"
            data-testid="preview-content"
            dir={lang === "ar" ? "rtl" : "ltr"}
          >
            {preview?.raw_text}
          </pre>
          {preview?.verified_references?.length > 0 && (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <h4 className="font-heading font-semibold mb-2 text-start">
                {lang === "ar" ? "المراجع المتحقَّقة عبر Crossref" : "Crossref-verified references"}
              </h4>
              <ol className="text-xs space-y-1 text-slate-700 text-start" data-testid="preview-refs">
                {preview.verified_references.map((r, i) => (
                  <li key={i} className="ps-4">
                    {r.apa || r.raw}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-start">{t("delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("delete_confirm_desc")}
              {pendingDelete?.title && (
                <span className="block mt-2 font-medium text-slate-900">
                  {pendingDelete.title}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" disabled={deleting}>
              {t("delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              {deleting ? t("loading") : t("delete_confirm_cta")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const UserDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  // Read ?tab=archive from URL so PaymentCallback's "متابعة طلبي" link lands
  // directly on the archive view instead of the empty new-order form.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "archive" ? "archive" : "new";
  const [activeTab, setActiveTab] = useState(initialTab);

  const onTabChange = (v) => {
    setActiveTab(v);
    // Keep the URL in sync so reloads / back-button preserve the chosen tab.
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      if (v === "archive") next.set("tab", "archive");
      else next.delete("tab");
      return next;
    }, { replace: true });
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-6 sm:py-10" data-testid="user-dashboard-page">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-start break-words">
            {user?.name ? `${user.name}` : t("nav_dashboard")}
          </h1>
          <p className="text-sm text-slate-500 text-start mt-1 break-all" data-testid="user-email-line">
            {user?.email}
          </p>
        </div>

        {user && !user.email_verified && (
          <Alert className="mb-6 bg-amber-50 border-amber-200" data-testid="email-unverified-banner">
            <AlertDescription className="text-amber-900">
              {t("verify_email_pending")}
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
          <TabsList className="w-full sm:w-auto grid grid-cols-2 sm:inline-flex h-auto sm:h-9 gap-1" data-testid="user-dashboard-tabs">
            <TabsTrigger value="new" className="h-11 sm:h-7 text-sm" data-testid="tab-new-order">{t("tab_new_order")}</TabsTrigger>
            <TabsTrigger value="archive" className="h-11 sm:h-7 text-sm" data-testid="tab-archive">{t("tab_archive")}</TabsTrigger>
          </TabsList>
          <TabsContent value="new" className="mt-6">
            <NewOrderTab />
          </TabsContent>
          <TabsContent value="archive" className="mt-6">
            <ArchiveTab />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default UserDashboard;
